import { type PriceInfo, type PriceInfoDTO, transformPrice } from "../../api/pna/PriceInfo";
import { BATCHING_INTERVAL, BATCHING_PNA_BATCHES, BATCHING_PRICE_ENABLED } from "../../utils/constants";
import { resolveApiUrl } from "../../utils/url/urlResolver";
import { ApiCallPriority, apiGET, apiPOST } from "./apiCallsService";
import { getCsrfValue } from "../../api/ApiHelper";
import { BatchSizeProvider } from "../batchSizeProvider";
import { toMapFn } from "../../utils/reducers";

export interface PriceApiData {
  productCode: string;
  priceReference?: string;
  packCode?: string;
}

interface PriceQueueElement {
  resolve: (data: PriceInfo[]) => void;
  reject: (data: unknown) => void;
  data: PriceApiData;
}

const generatePriceApiKey = (data: PriceApiData) => `${data.productCode}-${data.priceReference}-${data.packCode}`;

const P_CACHE: Dict<Promise<PriceInfo[]>> = {};
const queuedPCalls: PriceQueueElement[] = [];
let pGroupingScheduled: boolean = false;

export function requestPriceDataSingle(data: PriceApiData) {
  const cacheKey = generatePriceApiKey(data);
  let cacheEntry = P_CACHE[cacheKey];

  if (!cacheEntry) {
    const queueElement: Partial<PriceQueueElement> = { data };
    cacheEntry = new Promise((resolve, reject) => {
      queueElement.resolve = resolve;
      queueElement.reject = reject;
    });
    cacheEntry.finally(() => {
      delete P_CACHE[cacheKey];
    });
    const finalizedQueueElement = queueElement as PriceQueueElement;
    queuedPCalls.push(finalizedQueueElement);
    P_CACHE[cacheKey] = cacheEntry;

    if (!pGroupingScheduled) {
      pGroupingScheduled = true;
      window.setTimeout(dispatchApiCall, BATCHING_INTERVAL);
    }
  }

  return cacheEntry;
}

const batchSizeProvider = new BatchSizeProvider(BATCHING_PNA_BATCHES);

function dispatchApiCall() {
  const itemsToCall = queuedPCalls.slice(0, batchSizeProvider.getBatchSize());
  queuedPCalls.splice(0, itemsToCall.length);

  pGroupingScheduled = false;

  if (itemsToCall.length === 0) {
    return;
  }

  if (!BATCHING_PRICE_ENABLED) {
    for (const item of itemsToCall) {
      const promise = apiFetchRegularPriceSingle(item.data.productCode, item.data.priceReference, item.data.packCode);
      promise.then(item.resolve, item.reject);
    }
  } else {
    const promise = apiFetchRegularPriceMultiple(itemsToCall.map((item) => item.data));
    promise.then(
      (data) =>
        itemsToCall.forEach((item) => {
          const fetchedData = data[item.data.productCode];
          item.resolve(fetchedData);
        }),
      (error) => itemsToCall.forEach((item) => item.reject(error)),
    );
  }

  if (queuedPCalls.length > 0) {
    pGroupingScheduled = true;
    window.setTimeout(dispatchApiCall, BATCHING_INTERVAL);
  }
}

export async function apiFetchRegularPriceSingle(
  productCode: string,
  priceReference: string = "",
  packCode?: string,
): Promise<PriceInfo[]> {
  const singleApiUrl = resolveApiUrl("/pna/getPrice");
  const params = new URLSearchParams();
  if (priceReference) {
    params.append("priceReference", priceReference);
  }
  if (packCode) {
    params.append("packCode", packCode);
  }
  const paramsString = params.toString();
  const finalUrl = `${singleApiUrl}/${productCode}${paramsString ? `?${paramsString}` : ""}`;

  const response = await apiGET(finalUrl, {}, ApiCallPriority.LOW);
  if (!response.ok) {
    throw new Error("Error fetching data");
  }

  const dto: PriceInfoDTO[] = await response.json();
  return dto.map(transformPrice);
}

const multiApiUrl = resolveApiUrl("/pna/getPriceList");
export async function apiFetchRegularPriceMultiple(data: PriceApiData[]): Promise<Record<string, PriceInfo[]>> {
  const body = data.map((item) => ({
    code: item.productCode,
    priceReference: item.priceReference,
    packCode: item.packCode,
    quantity: 1,
  }));
  const response = await apiPOST(
    multiApiUrl,
    {
      body: JSON.stringify({ products: body }),
      cache: "no-cache",
      headers: {
        "content-type": "application/json",
        CSRFToken: getCsrfValue(),
      },
    },
    ApiCallPriority.LOW,
  );

  if (!response.ok) {
    throw new Error("Error fetching data");
  }

  const dto: PriceInfoDTO[][] = await response.json();
  const transformedData = dto.map(transformList);

  // noinspection UnnecessaryLocalVariableJS
  const record = toMapFn(
    transformedData.filter((list) => list.length > 0),
    (list) => list[0].code,
  );
  return record;
}

const transformList = (items: PriceInfoDTO[]) => items.map(transformPrice);
